/**
 * Pages/Panel/ReportesNomina/ReporteRepse.js
 *
 * Plantilla oficial de subcontratación de servicios especializados
 * (REPSE / ICSOE). Genera UN archivo con las dos hojas del formato:
 *
 *   1. "Subcontratacion de servicios es" — un renglón por Registro Patronal con
 *      trabajadores en el periodo. Razón social, RFC y RP salen del sistema; el
 *      objeto y la vigencia del contrato y los folios ICSOE se capturan en la
 *      pestaña "Datos del contrato" de esta misma pantalla; el folio, la fecha,
 *      el estatus y la actividad REPSE salen del catálogo de Registro Patronal.
 *
 *   2. "Datos Trabajadores Subcontra" — un renglón por trabajador con CFDI
 *      timbrado en el periodo, con la suma de sus días pagados.
 *
 * Los bloques literales de la plantilla viven en plantillaRepse.js.
 */

import { message, PageHeader, Select, Space, Spin, Tabs } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  InputItem,
  RangePickerItem,
  DatePickerItem,
  SelectItem,
  TableItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import { SI_NO_OPTIONS } from "../../../constants/nomina/repse";
import {
  NOMBRE_HOJA_CONTRATOS,
  HEADERS_CONTRATOS,
  ESPECIFICACIONES_CONTRATOS,
  BANNERS_CONTRATOS,
  MERGES_BANNER_CONTRATOS,
  NOTAS_CONTRATOS,
  ANCHOS_CONTRATOS,
  NOMBRE_HOJA_TRABAJADORES,
  HEADERS_TRABAJADORES,
  ESPECIFICACIONES_TRABAJADORES,
  BANNERS_TRABAJADORES,
  MERGES_BANNER_TRABAJADORES,
  ANCHOS_TRABAJADORES,
} from "./plantillaRepse";
import {
  bannerStyle,
  headerStyle,
  obligatorioStyle,
  guiaStyle,
  textoStyle,
  centroStyle,
  numeroStyle,
  monedaStyle,
  totalTextoStyle,
  totalNumeroStyle,
  celda,
  celdaNumero,
  celdaMoneda,
  filaDe,
} from "./estilosExcelNomina";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

class ReporteRepse extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    registrosPatronales: [],
    trabajadores: [],
    objetoRegistrosPatronales: null,
    // Lo capturado en la pestaña "Datos del contrato" al momento de consultar:
    // es lo que se escribe en el Excel, no viene de la base.
    datosContrato: {},
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        // El endpoint responde { registrosPatronales, success } y trae hasta 10,
        // de sobra para el filtro: una empresa rara vez tiene más registros.
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: res.data.registrosPatronales.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarReporte(values) {
    this.setState({ loading: true });
    const [fechaInicio, fechaFin] = values.periodo || [];
    axios
      .post(
        "reportesNomina/reporteRepse",
        {
          empresaId: this.props.empresaId,
          // El rango va contra Prenomina.fechaPago, que es una fecha-día: se
          // manda como "YYYY-MM-DD" para que parsearFechaDia lo ancle igual sin
          // depender de la zona horaria del navegador.
          fechaInicio: fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null,
          fechaFin: fechaFin ? fechaFin.format("YYYY-MM-DD") : null,
          registrosPatronales: values.registrosPatronales || [],
          departamentos: values.departamentos || [],
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            consultado: true,
            empresa: res.data.empresa,
            registrosPatronales: res.data.registrosPatronales || [],
            trabajadores: res.data.trabajadores || [],
            datosContrato: {
              objetoContrato: values.objetoContrato || "",
              inicioContrato: values.inicioContrato
                ? values.inicioContrato.format("DD/MM/YYYY")
                : "",
              conclusionContrato: values.conclusionContrato
                ? values.conclusionContrato.format("DD/MM/YYYY")
                : "",
              elementosPropios: values.elementosPropios || "",
              direccionSupervision: values.direccionSupervision || "",
              folioContratoIcsoe: values.folioContratoIcsoe || "",
              folioIcsoe: values.folioIcsoe || "",
              fechaPresentacionIcsoe: values.fechaPresentacionIcsoe
                ? values.fechaPresentacionIcsoe.format("DD/MM/YYYY")
                : "",
            },
          });
          if ((res.data.trabajadores || []).length === 0) {
            message.info("No hay trabajadores con CFDI timbrado en el periodo");
          }
        } else if (res.data.success === 1) {
          // Tope de filas: el backend dice cómo acotar la consulta.
          message.warning(res.data.message, 8);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargo bien el reporte");
        this.setState({ loading: false });
      });
  }

  // ── Hoja 1: contratos ──────────────────────────────────────────────────────
  hojaContratos() {
    const { empresa, registrosPatronales, datosContrato } = this.state;
    const columnas = HEADERS_CONTRATOS.length;
    const filas = [];
    const merges = [...MERGES_BANNER_CONTRATOS];

    // Filas 1-2: banners
    const textosFila1 = {};
    const textosFila2 = {};
    BANNERS_CONTRATOS.forEach((b) => {
      if (b.fila === 0) textosFila1[b.columna] = b.texto;
      else textosFila2[b.columna] = b.texto;
    });
    filas.push(filaDe(columnas, bannerStyle, textosFila1));
    filas.push(filaDe(columnas, bannerStyle, textosFila2));

    // Fila 3: encabezados. Fila 4: CAMPO OBLIGATORIO. Fila 5: especificaciones.
    filas.push(HEADERS_CONTRATOS.map((h) => celda(h, headerStyle)));
    filas.push(
      HEADERS_CONTRATOS.map((_, i) => celda(i === 0 ? "" : "CAMPO OBLIGATORIO", obligatorioStyle))
    );
    filas.push(ESPECIFICACIONES_CONTRATOS.map((t) => celda(t, guiaStyle)));

    registrosPatronales.forEach((registro, indice) => {
      filas.push([
        celdaNumero(indice + 1, centroStyle),
        celda(empresa?.nombre || "", textoStyle),
        celda(empresa?.rfc || "", centroStyle),
        celda(registro.rp, centroStyle),
        celda(datosContrato.objetoContrato, textoStyle),
        celda(datosContrato.inicioContrato, centroStyle),
        celda(datosContrato.conclusionContrato, centroStyle),
        celdaNumero(registro.numTrabajadores, centroStyle),
        celda(datosContrato.elementosPropios, centroStyle),
        celda(datosContrato.direccionSupervision, centroStyle),
        celda(registro.folioRepse, centroStyle),
        celda(registro.fechaRepse, centroStyle),
        celda(registro.estatusRepse, centroStyle),
        celda(registro.actividadRepse, textoStyle),
        celda(datosContrato.folioContratoIcsoe, centroStyle),
        celda(datosContrato.folioIcsoe, centroStyle),
        celda(datosContrato.fechaPresentacionIcsoe, centroStyle),
      ]);
    });

    // Renglón de Totales. En la plantilla original sólo trae las etiquetas (en
    // las columnas C y G) y ninguna cifra; aquí se llenan de verdad.
    const totalTrabajadores = registrosPatronales.reduce(
      (acumulado, r) => acumulado + (r.numTrabajadores || 0),
      0
    );
    const filaTotales = filaDe(columnas, totalTextoStyle);
    filaTotales[0] = celdaNumero(registrosPatronales.length, totalNumeroStyle);
    filaTotales[2] = celda("Totales", totalTextoStyle);
    filaTotales[6] = celda("Totales", totalTextoStyle);
    filaTotales[7] = celdaNumero(totalTrabajadores, totalNumeroStyle);
    filas.push(filaTotales);

    // Notas del pie
    filas.push(filaDe(columnas, {}));
    const filaNotas = filaDe(columnas, {});
    NOTAS_CONTRATOS.forEach((nota) => {
      filaNotas[nota.columna] = celda(nota.texto, { font: { italic: true, sz: 9 } });
    });
    filas.push(filaNotas);

    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!merges"] = merges;
    hoja["!cols"] = ANCHOS_CONTRATOS.map((wch) => ({ wch }));
    hoja["!rows"] = [{ hpt: 30 }, { hpt: 20 }, { hpt: 60 }, { hpt: 18 }, { hpt: 70 }];
    return hoja;
  }

  // ── Hoja 2: trabajadores ───────────────────────────────────────────────────
  hojaTrabajadores() {
    const { trabajadores, registrosPatronales, datosContrato } = this.state;
    const columnas = HEADERS_TRABAJADORES.length;
    const filas = [];

    const textosBanner = {};
    BANNERS_TRABAJADORES.forEach((b) => {
      textosBanner[b.columna] = b.texto;
    });
    filas.push(filaDe(columnas, bannerStyle, textosBanner));

    filas.push(HEADERS_TRABAJADORES.map((h) => celda(h, headerStyle)));
    filas.push(
      HEADERS_TRABAJADORES.map((_, i) => celda(i === 0 ? "" : "CAMPO OBLIGATORIO", obligatorioStyle))
    );
    filas.push(ESPECIFICACIONES_TRABAJADORES.map((t) => celda(t, guiaStyle)));

    // Los 4 datos REPSE se repiten en cada renglón de trabajador: son del
    // registro patronal bajo el que se le pagó.
    const repsePorRegistro = new Map(
      registrosPatronales.map((r) => [r.rp, r])
    );

    trabajadores.forEach((trabajador, indice) => {
      const registro = repsePorRegistro.get(trabajador.rp);
      filas.push([
        celdaNumero(indice + 1, centroStyle),
        celda(trabajador.rp, centroStyle),
        celda(trabajador.apellidoPaterno, textoStyle),
        celda(trabajador.apellidoMaterno, textoStyle),
        celda(trabajador.nombres, textoStyle),
        celda(trabajador.nss, centroStyle),
        celda(trabajador.rfc, centroStyle),
        celda(trabajador.curp, centroStyle),
        celda(trabajador.puesto, textoStyle),
        celda(trabajador.tipoPersonal, centroStyle),
        celdaMoneda(trabajador.sbc, monedaStyle),
        celda(trabajador.fechaIngreso, centroStyle),
        celdaNumero(trabajador.diasTrabajados, numeroStyle),
        celda(registro?.folioRepse || "", centroStyle),
        celda(registro?.fechaRepse || "", centroStyle),
        celda(registro?.estatusRepse || "", centroStyle),
        celda(registro?.actividadRepse || "", textoStyle),
        celda(datosContrato.folioContratoIcsoe, centroStyle),
        celda(datosContrato.folioIcsoe, centroStyle),
        celda(datosContrato.fechaPresentacionIcsoe, centroStyle),
      ]);
    });

    const totalDias = trabajadores.reduce(
      (acumulado, t) => acumulado + (t.diasTrabajados || 0),
      0
    );
    const filaTotales = filaDe(columnas, totalTextoStyle);
    filaTotales[0] = celdaNumero(trabajadores.length, totalNumeroStyle);
    filaTotales[2] = celda("Totales", totalTextoStyle);
    filaTotales[12] = celdaNumero(Math.round(totalDias * 100) / 100, totalNumeroStyle);
    filas.push(filaTotales);

    const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
    hoja["!merges"] = MERGES_BANNER_TRABAJADORES;
    hoja["!cols"] = ANCHOS_TRABAJADORES.map((wch) => ({ wch }));
    hoja["!rows"] = [{ hpt: 22 }, { hpt: 46 }, { hpt: 18 }, { hpt: 70 }];
    return hoja;
  }

  descargarExcel() {
    const libro = XLSXStyle.utils.book_new();
    // El nombre de hoja de Excel topa en 31 caracteres: los dos de la plantilla
    // ya vienen recortados a esa longitud.
    XLSXStyle.utils.book_append_sheet(libro, this.hojaContratos(), NOMBRE_HOJA_CONTRATOS);
    XLSXStyle.utils.book_append_sheet(libro, this.hojaTrabajadores(), NOMBRE_HOJA_TRABAJADORES);
    XLSXStyle.writeFile(libro, `Plantilla_REPSE_${moment().format("MMM_YYYY").toUpperCase()}.xlsx`);
  }

  render() {
    const { loading, consultado, trabajadores, registrosPatronales } = this.state;

    // TableItem fuerza tableLayout fixed e ignora `scroll`, asi que en pantalla
    // van solo las columnas indispensables; el detalle completo va al Excel.
    const columns = [
      { title: "RP", dataIndex: "rp", key: "rp" },
      {
        title: "Trabajador",
        dataIndex: "trabajador",
        key: "trabajador",
        render: (v, record) =>
          [record.apellidoPaterno, record.apellidoMaterno, record.nombres]
            .filter(Boolean)
            .join(" "),
      },
      { title: "NSS", dataIndex: "nss", key: "nss" },
      { title: "RFC", dataIndex: "rfc", key: "rfc" },
      { title: "Puesto", dataIndex: "puesto", key: "puesto" },
      {
        title: "Tipo de Personal",
        dataIndex: "tipoPersonal",
        key: "tipoPersonal",
        render: (v) => v || "—",
      },
      {
        title: "SBC",
        dataIndex: "sbc",
        key: "sbc",
        align: "right",
        render: (v) => dineroDisabledMask(v || 0),
      },
      {
        title: "Fecha de Ingreso",
        dataIndex: "fechaIngreso",
        key: "fechaIngreso",
        align: "center",
      },
      {
        title: "Días",
        dataIndex: "diasTrabajados",
        key: "diasTrabajados",
        align: "right",
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteRepse"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
            elementosPropios: "SI",
            direccionSupervision: "NO",
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Reporte REPSE"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {trabajadores.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <FormItem name="periodo" required noStyle>
                    <RangePickerItem />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Datos del contrato">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                    <FormItem name="objetoContrato" label="Objeto del contrato" margin>
                      <InputItem placeholder="De acuerdo al contrato" style={{ width: 260 }} />
                    </FormItem>
                    <FormItem name="inicioContrato" label="Inicio del contrato" margin>
                      <DatePickerItem />
                    </FormItem>
                    <FormItem name="conclusionContrato" label="Conclusión del contrato" margin>
                      <DatePickerItem />
                    </FormItem>
                    <FormItem
                      name="elementosPropios"
                      label="¿Con elementos propios del prestador?"
                      margin
                    >
                      <SelectItem width={110} placeholder="SI / NO">
                        {SI_NO_OPTIONS.map((value) => (
                          <Option value={value} key={value}>
                            {value}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                    <FormItem
                      name="direccionSupervision"
                      label="¿El beneficiario dirige o supervisa?"
                      margin
                    >
                      <SelectItem width={110} placeholder="SI / NO">
                        {SI_NO_OPTIONS.map((value) => (
                          <Option value={value} key={value}>
                            {value}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                    <FormItem
                      name="folioContratoIcsoe"
                      label="Folio del contrato en ICSOE"
                      margin
                    >
                      <InputItem placeholder="Ej: A21P0000001" style={{ width: 180 }} />
                    </FormItem>
                    <FormItem name="folioIcsoe" label="Folio ICSOE" margin>
                      <InputItem placeholder="Ej: N21P0000001" style={{ width: 180 }} />
                    </FormItem>
                    <FormItem
                      name="fechaPresentacionIcsoe"
                      label="Fecha de presentación ICSOE"
                      margin
                    >
                      <DatePickerItem />
                    </FormItem>
                  </div>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {trabajadores.length} trabajador(es) en {registrosPatronales.length} registro(s)
                  patronal(es).
                </p>
                <TableItem
                  rowKey={(record) => record.empleadoId}
                  columns={columns}
                  dataSource={trabajadores}
                  size="small"
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteRepse);
